import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Shift} from '@app/core/models/shift.model';
import {User} from '@app/core/models/user.model';
import {IQOSelectData} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-select-data';
import {Group} from '@app/core/models/group.model';
import {Subject, timer} from 'rxjs';
import {convertModelsToSelectData, EQORebrandColor, EQORebrandIcon} from '@quickorder/angular-web-lib';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {SnackBar} from '@app/core/services/snackbar.service';
import * as moment from 'moment';
import {Moment} from 'moment';
import {PunchIn, PunchOut} from '@app/core/store/actions/dashboard.actions';
import {AuthService} from '@app/core/services/auth.service';
import {takeUntil} from 'rxjs/operators';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

@Component({
	selector: 'app-punch-popup',
	templateUrl: './punch-popup.component.html',
	styleUrls: ['./punch-popup.component.scss'],
})
export class PunchPopupComponent implements OnInit, OnDestroy {
	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;

	public groupSelectOptions: IQOSelectData<Group>[] = [];
	public dateNow: Moment = moment();
	public loadingLocation: boolean;
	public location: any;

	public form: FormGroup;
	private _destroy$: Subject<void> = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<PunchPopupComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { punchedIn: boolean, shift: Shift, user: User, groups: Group[] },
		private store: Store<IAppState>,
		private snackBar: SnackBar,
		private dialog: MatDialog,
		private authService: AuthService,
		private formBuilder: FormBuilder,
	) {
	}

	public get selectedGroupControl(): FormControl {
		return this.form.get('selectedGroup') as FormControl;
	}

	public ngOnInit(): void {
		this.initForm();
		this.initSubscriptions();
		this.groupSelectOptions = convertModelsToSelectData(this.data.groups, 'group_name');
		this.getLocation();
	}

	public close(): void {
		this.dialogRef.close();
	}

	public getLocation(): void {
		this.loadingLocation = true;
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(position => {
					this.location = position.coords;
					this.loadingLocation = false;
				},
				() => {
					this.snackBar.error('COULD_NOT_GET_LOCATION_YOU_CAN_STILL_PUNCH_IN');
					this.loadingLocation = false;
				},
				{
					timeout: 10 * 1000,
					maximumAge: 30000,
					enableHighAccuracy: true,
				});
		} else {
			this.snackBar.error('NO_GEO_IN_BROWSER_NEEDS_APPROVAL');
			this.loadingLocation = false;
		}
	}

	public punch(): void {
		const formValue = this.form.value;
		if (this.data.punchedIn) {
			const data = (this.location && this.location.latitude)
				? {
					lat: this.location.latitude,
					lng: this.location.longitude,
					punch_note: formValue.punch_note,
				}
				: {punch_note: formValue.punch_note};
			this.store.dispatch(new PunchOut(data));
		} else {
			const data = (this.location && this.location.latitude)
				? {
					lat: this.location.latitude,
					lng: this.location.longitude,
					group_id: Number(this.selectedGroupControl.value.data.id),
					department_id: this.authService.selectedDepartment.id,
				}
				: {
					group_id: Number(this.selectedGroupControl.value.data.id),
					department_id: this.authService.selectedDepartment.id
				};
			this.store.dispatch(new PunchIn(data));
		}
	}

	public ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	private initForm(): void {
		this.form = this.formBuilder.group({
			selectedGroup: [null, Validators.required],
			punch_note: [null],
		});
		if (this.data.shift) {
			this.selectedGroupControl.patchValue(
				{
					data: this.data.shift.group,
					text: this.data.shift.group?.group_name,
				},
			);
			this.selectedGroupControl.disable();
		}
	}

	private initSubscriptions(): void {
		timer(0, 1000)
			.pipe(takeUntil(this._destroy$))
			.subscribe(() => {
				this.dateNow = moment();
			});
	}
}
